/// The eight RYA knots required for Day Skipper / Yachtmaster.
///
/// Reference-only catalogue — each entry teaches when to use the knot
/// and how to tie it, but there's no flashcards game (yet). Photos
/// would be the next iteration; the step-by-step text is enough to
/// jog the memory of someone who has tied it before.
///
/// Category buckets help the reader scan: stopper (figure-of-eight),
/// hitches (clove, rolling, round-turn-and-two-half-hitches), loops
/// (bowline), bends (reef, sheet, double sheet).

export type KnotCategory = "stopper" | "hitch" | "loop" | "bend";

export function categoryLabel(c: KnotCategory): string {
  switch (c) {
    case "stopper":
      return "Stopper";
    case "hitch":
      return "Hitch (rope to object)";
    case "loop":
      return "Loop";
    case "bend":
      return "Bend (rope to rope)";
  }
}

export const KNOT_CATEGORIES: readonly KnotCategory[] = [
  "stopper",
  "hitch",
  "loop",
  "bend",
];

export interface Knot {
  /// Stable identifier.
  id: string;
  /// Display name.
  name: string;
  category: KnotCategory;
  /// One-line summary of when to use it.
  use: string;
  /// Step-by-step tying instructions, one step per array entry.
  steps: readonly string[];
  /// Optional extra-credit notes — limitations, common mistakes.
  notes?: string;
}

export const KNOTS: Readonly<Record<string, Knot>> = Object.freeze({
  figure_eight: {
    id: "figure_eight",
    name: "Figure of Eight",
    category: "stopper",
    use: "Stops the end of a sheet or halyard running out through a block or fairlead.",
    steps: [
      "Form a loop in the working end.",
      "Pass the working end behind the standing part.",
      "Bring the working end up and back down through the loop.",
      "Pull tight — the result looks like the figure 8.",
    ],
    notes:
      "Preferred over a simple overhand because it doesn't jam under load.",
  },
  round_turn_two_half_hitches: {
    id: "round_turn_two_half_hitches",
    name: "Round Turn and Two Half Hitches",
    category: "hitch",
    use: "Securing a line to a post, ring, or rail under load.",
    steps: [
      "Pass the working end fully around the post once, then again — that's the round turn.",
      "Take the working end across the standing part.",
      "Pass it under and through to make the first half hitch.",
      "Repeat the same motion in the same direction for the second half hitch.",
      "Pull both half hitches tight against the round turn.",
    ],
    notes:
      "The round turn carries the load; the hitches just stop the end pulling through. Easy to untie even after heavy loading.",
  },
  clove_hitch: {
    id: "clove_hitch",
    name: "Clove Hitch",
    category: "hitch",
    use: "Quick attachment to a rail — fenders, awning lines, light loads only.",
    steps: [
      "Pass the working end over and around the rail.",
      "Cross the working end over the standing part.",
      "Pass it under and around the rail a second time, going under the cross.",
      "Pull both ends to tighten.",
    ],
    notes:
      "Slips under variable load — back up with a half hitch if you can't watch it.",
  },
  rolling_hitch: {
    id: "rolling_hitch",
    name: "Rolling Hitch",
    category: "hitch",
    use: "Attaching a line to a spar or another rope so it won't slip along under load.",
    steps: [
      "Pass the working end around the spar, crossing over the standing part on the load side.",
      "Take a second turn beside the first, again crossing over the load side — both turns sit on the load side.",
      "Take a third turn on the other side of the standing part and finish with a half hitch.",
      "Push the turns tight together and load it.",
    ],
    notes:
      "Used to take load off a jammed winch — tie onto the loaded sheet, lead the rolling-hitch line to a spare winch.",
  },
  bowline: {
    id: "bowline",
    name: "Bowline",
    category: "loop",
    use: "Non-slipping loop at the end of a line — the 'king of knots'.",
    steps: [
      "Form a small loop in the standing part — \"the rabbit hole\".",
      "Bring the working end up through the loop from underneath.",
      "Pass it behind the standing part — \"round the tree\".",
      "Bring it back down through the same loop — \"and back down the hole\".",
      "Pull the standing part to set the knot.",
    ],
    notes:
      "Won't slip under load. Can shake loose when unloaded — back up with a stopper knot for safety-critical use.",
  },
  reef_knot: {
    id: "reef_knot",
    name: "Reef Knot",
    category: "bend",
    use: "Joining two lines of equal thickness — classically for reefing a sail.",
    steps: [
      "Take an end in each hand.",
      "Pass the left over the right and tuck under — like the first step of tying a shoelace.",
      "Now pass the right (which is now on top) over the left and tuck under.",
      "Pull both standing parts to set.",
    ],
    notes:
      "Mnemonic: \"left-over-right, right-over-left\". Don't use to join ropes of unequal thickness — use a sheet bend instead.",
  },
  sheet_bend: {
    id: "sheet_bend",
    name: "Sheet Bend",
    category: "bend",
    use: "Joining two lines, especially of different thickness or material.",
    steps: [
      "Form a bight (a U-shape) in the thicker rope's end.",
      "Pass the working end of the thinner rope up through the bight from underneath.",
      "Take it around the back of the bight (under both legs of the U).",
      "Tuck it back under itself on the same side it came up — both free ends finish on the same side.",
      "Pull tight.",
    ],
    notes:
      "Both free ends must come out on the same side. If they're on opposite sides you've tied a left-handed sheet bend — it slips and isn't safe to load.",
  },
  double_sheet_bend: {
    id: "double_sheet_bend",
    name: "Double Sheet Bend",
    category: "bend",
    use: "As the sheet bend, but more secure on slippery synthetic or wet line.",
    steps: [
      "Start a sheet bend as above.",
      "Before pulling tight, take the working end around the back of the bight a second time.",
      "Tuck it under itself once more, again finishing on the same side as the bight's tail.",
      "Pull tight.",
    ],
    notes:
      "The extra turn gives more friction — preferred whenever the lines are slick or under high load.",
  },
  overhand: {
    id: "overhand",
    name: "Overhand Knot",
    category: "stopper",
    use: "Simplest stopper knot — quick block on a small line or first half of many other knots.",
    steps: [
      "Form a loop in the working end so it crosses the standing part.",
      "Pass the working end through the loop.",
      "Pull both ends to tighten.",
    ],
    notes:
      "Jams hard under load — for a stopper that releases easily, use a figure of eight instead.",
  },
  cleat_hitch: {
    id: "cleat_hitch",
    name: "Cleat Hitch",
    category: "hitch",
    use: "Securing a halyard, sheet, or dock line to a horn cleat. The standard cleat tie-off.",
    steps: [
      "Take a full turn around the base of the cleat, leading from the far horn around to the near horn.",
      "Cross diagonally over the top of the cleat in a figure-of-eight around the horns.",
      "Finish with a single locking hitch — a loop with the working end tucked through, snugged against the last diagonal.",
    ],
    notes:
      "Don't use more than one locking hitch on a halyard — extras can jam. Two figure-of-eight crosses are plenty for normal loads.",
  },
  timber_hitch: {
    id: "timber_hitch",
    name: "Timber Hitch",
    category: "hitch",
    use: "Lashing a line to a spar, log, or anything you'll be dragging or hoisting along its length.",
    steps: [
      "Pass the working end around the spar.",
      "Take it once around the standing part.",
      "Wrap the working end around itself three or more times, lying along the spar.",
      "Pull the standing part — the wraps trap themselves against the spar.",
    ],
    notes:
      "Holds well under tension, releases instantly when load comes off — perfect for hoisting masts or towing a length of timber.",
  },
});

export const KNOT_IDS: readonly string[] = Object.keys(KNOTS);
